'use client';
import React from 'react';
import { noop } from '@benzinga/utils';
import { GlobalSettingManifest } from './globalSettingsContext';
import { WidgetId } from './context';

export const WidgetParametersContext = React.createContext<WidgetParametersContextProps>({
  parameters: undefined,
  setParameters: noop,
  setter: {},
});

export type WidgetParametersSetters<T extends object = object> = {
  [K in keyof T]-?: (callback: T[K] | ((value: T[K]) => T[K])) => void;
};
export interface WidgetParametersContextProps<T extends object = object> {
  parameters?: T;
  setParameters: (callback: (parameters: T) => T) => void;
  setter: WidgetParametersSetters<T>;
}

export const WidgetParametersContextProvider: React.FC<
  React.PropsWithChildren<Partial<Omit<WidgetParametersContextProps, 'setter'>>>
> = props => {
  return (
    <WidgetParametersContext.Provider
      value={React.useMemo(
        () => ({
          parameters: props.parameters ?? undefined,
          setParameters: props.setParameters ?? noop,
          setter: Object.keys(props.parameters ?? {}).reduce((acc, key) => {
            acc[key] = (value: unknown | ((value: unknown) => unknown)) => {
              const setParameters = props.setParameters;
              if (typeof value === 'function') {
                setParameters?.(old => ({ ...old, [key]: value(old[key]) }));
              } else {
                setParameters?.(old => ({ ...old, [key]: value }));
              }
            };
            return acc;
          }, {}),
        }),
        [props.parameters, props.setParameters],
      )}
    >
      {props.children}
    </WidgetParametersContext.Provider>
  );
};

export const useWidgetParameters = <T extends GlobalSettingManifest>(
  manifest: T,
): Required<WidgetParametersContextProps<T['defaultWidgetParameters']>> => {
  const params = React.useContext(WidgetParametersContext) as WidgetParametersContextProps<
    T['defaultWidgetParameters']
  >;
  return React.useMemo(
    () => ({ ...params, parameters: params.parameters ?? manifest.defaultWidgetParameters }),
    [manifest.defaultWidgetParameters, params],
  );
};

export interface GenericWidget<Type extends string = string, Data extends object = object> {
  parameters: Data;
  type: Type;
  widgetId: WidgetId;
}
